"use client";

import { ArrowLeft, ChevronDown, Check } from "lucide-react";
import { useState, useRef, useEffect } from "react";

export interface SmartfloLoginCampaign {
  id: number;
  name: string;
}

type CampaignSelectionFormProps = {
  userName: string;
  campaigns: SmartfloLoginCampaign[];
  selectedCampaignId: number | null;
  onSelectCampaign: (id: number) => void;
  onProceed: () => void;
  onBack: () => void;
  isLoading?: boolean;
};

export default function CampaignSelectionForm({
  userName,
  campaigns,
  selectedCampaignId,
  onSelectCampaign,
  onProceed,
  onBack,
  isLoading = false,
}: CampaignSelectionFormProps) {
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  const selectedCampaign = campaigns.find((c) => c.id === selectedCampaignId) || campaigns[0];

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setDropdownOpen(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleSelect = (id: number) => {
    onSelectCampaign(id);
    setDropdownOpen(false);
  };

  return (
    <div className="w-full space-y-6">
      <div className="text-center space-y-1">
        <h2 className="text-2xl font-bold tracking-tight text-[var(--text-strong)]">
          Campaign selection
        </h2>
        <p className="text-sm text-[var(--text-muted)] font-medium">
          Welcome, {userName || "Agent"}
        </p>
      </div>

      <div className="space-y-4">
        <div className="relative" ref={dropdownRef}>
          <button
            type="button"
            onClick={() => setDropdownOpen((prev) => !prev)}
            className="w-full flex items-center justify-between h-11 px-3.5 rounded-lg border border-[var(--border)] bg-[var(--surface)] text-left text-sm font-medium text-[var(--text-strong)] shadow-sm hover:border-[var(--border-strong)] transition-all focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500"
          >
            <span className="truncate">{selectedCampaign ? selectedCampaign.name : "Select a Campaign"}</span>
            <ChevronDown className={`h-4 w-4 text-[var(--text-muted)] transition-transform duration-200 ${dropdownOpen ? "rotate-180" : ""}`} />
          </button>

          {dropdownOpen && (
            <div className="absolute z-50 mt-1.5 w-full rounded-lg border border-[var(--border)] bg-[var(--surface)] p-1 shadow-lg max-h-60 overflow-y-auto">
              {campaigns.map((camp) => {
                const isSelected = selectedCampaign && selectedCampaign.id === camp.id;
                return (
                  <button
                    key={camp.id}
                    type="button"
                    onClick={() => handleSelect(camp.id)}
                    className={`w-full flex items-center justify-between px-3 py-2 text-xs font-medium rounded-md transition-colors text-left ${
                      isSelected
                        ? "bg-blue-500/10 text-blue-600 dark:text-blue-400 font-semibold"
                        : "text-[var(--text-muted)] hover:bg-[var(--surface-muted)] hover:text-[var(--text-strong)]"
                    }`}
                  >
                    <span className="truncate">{camp.name}</span>
                    {isSelected && <Check className="h-3.5 w-3.5 text-blue-600 dark:text-blue-400 shrink-0 ml-2" />}
                  </button>
                );
              })}
            </div>
          )}
        </div>

        <button
          type="button"
          onClick={onProceed}
          disabled={isLoading || !selectedCampaign}
          className="w-full h-11 rounded-lg bg-blue-600 hover:bg-blue-700 text-white font-semibold text-sm transition-all shadow-sm flex items-center justify-center gap-2 disabled:opacity-60 disabled:cursor-not-allowed cursor-pointer"
        >
          {isLoading ? (
            <div className="h-4 w-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
          ) : (
            "Proceed to panel"
          )}
        </button>
      </div>

      <div className="flex justify-center pt-2">
        <button
          type="button"
          onClick={onBack}
          title="Back to login"
          className="h-10 w-10 rounded-full border border-[var(--border)] bg-[var(--surface-muted)] hover:bg-[var(--surface)] text-[var(--text-muted)] hover:text-[var(--text-strong)] flex items-center justify-center transition-all shadow-sm cursor-pointer"
        >
          <ArrowLeft className="h-4 w-4" />
        </button>
      </div>
    </div>
  );
}
